import {
	Directive,
	Input,
	ElementRef,
	OnInit,
	forwardRef,
	OnDestroy,
	Renderer2,
	HostListener,
	NgZone,
	AfterViewInit
} from "@angular/core";
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from "@angular/forms";

// @ts-ignore
import Cleave from "cleave.js";

/* eslint-disable  @typescript-eslint/no-explicit-any */
export const VALUE_ACCESSOR: any = {
	provide: NG_VALUE_ACCESSOR,
	useExisting: forwardRef(() => BpTypeDirective),
	multi: true
};

@Directive({
	// tslint:disable-next-line:directive-selector
	selector: "[bp-type]",
	providers: [VALUE_ACCESSOR],
	standalone: false
})
export class BpTypeDirective implements ControlValueAccessor, OnDestroy, OnInit, AfterViewInit {
	@Input()
	public precision: any = null;

	@Input()
	public delimiter: string = this.determinateLocalDecimalSeparator();

	private predefinedOptions: { [key: string]: any } = {
		fiat: {
			numeral: true,
			numeralDecimalScale: 2,
			delimiter: "",
			numeralDecimalMark: this.delimiter,
			numeralPositiveOnly: true,
			placeholder: "0" + this.delimiter + "00"
		},
		cryptocoin: {
			numeral: true,
			numeralDecimalScale: 8,
			numeralDecimalMark: this.delimiter,
			delimiter: "",
			numeralPositiveOnly: true,
			placeholder: "0" + this.delimiter + "000"
		},
		zeroPrecision: {
			numeral: true,
			numeralDecimalScale: 0,
			numeralDecimalMark: this.delimiter,
			delimiter: "",
			numeralPositiveOnly: true,
			placeholder: "0"
		},
		"cryptocoin-max": {
			numeral: true,
			numeralDecimalScale: 8,
			numeralDecimalMark: this.delimiter,
			delimiter: "",
			numeralPositiveOnly: true,
			placeholder: "0" + this.delimiter + "000"
		},
		voucher: {
			numericOnly: true,
			delimiter: "-",
			blocks: [4, 4, 4, 4],
			uppercase: true,
			delimiterLazyShow: true
		},
		tel: {
			inputType: "tel",
			numericOnly: true,
			delimiter: " ",
			blocks: [3, 4, 4, 4],
			delimiterLazyShow: true
		},
		text: {
			inputType: "text"
		},
		number: {
			inputType: "number"
		},
		email: {
			inputType: "email"
		},
		search: {
			inputType: "search"
		},
		"2fa": {
			inputType: "tel",
			numericOnly: true,
			delimiter: " ",
			blocks: [3, 3],
			delimiterLazyShow: true
		},
		sms: {
			inputType: "sms",
			numericOnly: true,
			delimiter: " ",
			blocks: [6],
			delimiterLazyShow: true
		},
		"sms-protected": {
			inputType: "password",
			numericOnly: true,
			delimiter: " ",
			blocks: [4],
			delimiterLazyShow: true
		},
		birthdate: {
			delimiter: ".",
			date: true,
			datePattern: ["d", "m", "Y"],
			placeholder: "dd.mm.yyyy",
			delimiterLazyShow: true
		},
		"taxes-cryptocoin": {
			numeral: true,
			numeralDecimalScale: 8,
			numeralDecimalMark: this.delimiter,
			delimiter: "",
			numeralPositiveOnly: true
		}
	};

	private options: any;
	private cleave: Cleave;
	private elem: any;
	private type: any;

	@Input("bp-type")
	public set bpType(options: any) {
		if (!options) {
			return;
		}

		this.type = options;

		if (typeof options === "string" && this.predefinedOptions[options]) {
			this.options = this.predefinedOptions[options];
			if (this.regionCode && options === "tel") {
				this.options.regionCode = this.regionCode;
			}

			if (this.options.numeral) {
				this.options.numeralDecimalMark = this.determinateLocalDecimalSeparator();
			}
			this.options.numeralDecimalScale = this.precision || this.options.numeralDecimalScale;
		} else {
			this.options = options;
		}

		this.createInputFormatter(this.el, this.options);
	}

	// @ts-ignore
	@Input("phone-region") public regionCode: string;
	// @ts-ignore
	@Input("focus") public focus: boolean;
	@Input("formatted-output") public formattedOutput = true;

	public get bpType(): any {
		return this.options;
	}

	public onChange: (_: any) => void;

	@HostListener("blur", []) public onTouched: () => void = () => {
		return;
	};

	public constructor(
		private renderer: Renderer2,
		private el: ElementRef,
		private ngZone: NgZone
	) {}

	@HostListener("input", ["$event.target.value"])
	public writeValue(obj: any): void {
		if (this.cleave && obj != undefined) {
			this.cleave.setRawValue(obj);
		} else if (obj != undefined) {
			this.renderer.setProperty(this.el.nativeElement, "value", obj);
		}

		if (this.onChange && obj != undefined) {
			this.onChange(obj);
		}
	}

	public setDisabledState?(isDisabled: boolean): void {
		this.el.nativeElement.disabled = isDisabled;
	}

	public registerOnChange(fn: (_: any) => void): void {
		this.onChange = (obj: any) => {
			if (this.cleave) {
				fn(
					this.formattedOutput === null || this.formattedOutput
						? this.cleave.getFormattedValue()
						: this.cleave.getRawValue()
				);
			} else {
				fn(obj);
			}
		};
	}

	public registerOnTouched(fn: () => void): void {
		this.onTouched = fn;
	}

	private createInputFormatter(el: ElementRef, options: any): void {
		if (el) {
			if (this.cleave) {
				this.cleave.destroy();
			}
			if (options == "date" || options == "phone" || options == "tel") {
				el.nativeElement.setAttribute("type", options);

				if (options == "date") {
					el.nativeElement.setAttribute("max", "9999-12-31");
				}

				return;
			}

			if (
				options.inputType &&
				(options.inputType == "text" ||
					options.inputType == "email" ||
					options.inputType == "number" ||
					options.inputType == "search")
			) {
				el.nativeElement.setAttribute("type", options.inputType);
				return;
			}

			if (options.inputType) {
				el.nativeElement.setAttribute("type", options.inputType);
			}

			if (options.placeholder) {
				el.nativeElement.setAttribute("placeholder", options.placeholder);
			}
			this.cleave = new Cleave(this.el.nativeElement, options);
		}
	}

	public ngOnInit(): void {
		if (this.focus) {
			this.setFocus(this.el);
		}
	}

	private setFocus(el: ElementRef): void {
		this.ngZone.runOutsideAngular(() => {
			setTimeout(() => {
				el.nativeElement.focus();
			}, 0);
		});
	}

	public ngOnDestroy(): void {
		if (this.cleave) {
			this.cleave.destroy();
		}
	}

	private determinateLocalDecimalSeparator(): string {
		return (1.1).toLocaleString([], { localeMatcher: "lookup" }).substring(1, 2);
	}

	public ngAfterViewInit(): void {
		const options = this.type;

		if (!options) {
			return;
		}

		if (typeof options == "string" && this.predefinedOptions[options]) {
			this.options = this.predefinedOptions[options];
			if (this.regionCode && options == "tel") {
				this.options.regionCode = this.regionCode;
			}

			if (this.options.numeral) {
				this.options.numeralDecimalMark = this.determinateLocalDecimalSeparator();
			}
			this.options.numeralDecimalScale = this.precision || this.options.numeralDecimalScale;
		} else {
			this.options = options;
		}

		this.createInputFormatter(this.el, this.options);
	}
}
